import { ICalendarHeader, ICalendarItemData } from 'Calendar/types';
import { Avatar, Popover, Row } from 'antd';
import Icon from 'assets/Icons';
import Text from 'components/Text';
import first from 'lodash/first';
import get from 'lodash/get';
import React, { CSSProperties, memo, useCallback, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { FORMAT_TIME } from 'utils/format';
import { momentTimezone } from 'utils/time';
import Appointment from './Appointment';
import { calendarDOM } from 'Calendar/styles';
import { callerSelectActive } from 'Calendar/caller-booking-selection';
import { IAppointmentItemData } from 'types/appointment';
import { RepeatIcon } from 'assets/appointment';
import { AppointmentSourceIcon } from 'Calendar/appointment-source-icon';
import { CellRange, COMPACT_CARD_ZOOM, computeSlotPosition, shouldUseGrid, sortByStart } from './multi-layout';
import { shouldFlipPopupLeft } from 'Calendar/popup-placement';

const overlayInnerStyle: CSSProperties = { borderRadius: 0, boxShadow: 'none', padding: 0, background: 'transparent' };
// PopoverAppointmentStyled is `width: 50vh; max-width: 32rem`.
const popoverWidth = () => Math.min(window.innerHeight / 2, 512);
type IMultiAppointmentProps = {
  data: ICalendarItemData[];
  headerData?: ICalendarHeader;
  isMonthView?: boolean;
  dateOfMonth?: string;
  disabled?: boolean;
  /** Day view only: render 2-4 appointments side by side instead of the summary list box. */
  split?: boolean;
  /** Time window of the merged cell; with exactly two cards each one sits at its own time offset. */
  cellRange?: CellRange;
};
const MultiAppointment: React.FC<IMultiAppointmentProps> = ({
  data = [],
  headerData,
  isMonthView,
  disabled,
  split,
  cellRange,
}) => {
  const [open, setOpen] = useState(false);
  const cellRef = useRef<HTMLDivElement>(null);

  const appointments = useMemo(() => sortByStart(data), [data]);

  const onStopPropagation = useCallback((e: React.MouseEvent) => e.stopPropagation(), []);
  const onClickOpen = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setOpen(true);
  }, []);

  const content = useMemo(() => {
    const time = get(first(data), 'data.startTime');

    return (
      <PopoverAppointmentStyled>
        <button className="head" onClick={() => setOpen(false)}>
          <Icon type='close' />
        </button>
        <div className="body">
          <Text variant='H5' color='text_3' >Multi - Booking</Text>
          <div className="divider" />
          <div className='row-item'><Icon type='time' /> <Text variant='H9' color='text_3'>{time ? momentTimezone(time).format('MM-DD-YYYY HH:mm') : null}</Text></div>
          {headerData && headerData.type === 'staff' && <div className='row-item'>
            <Avatar size={'small'} style={{ backgroundColor: '#f56a00' }}>{headerData.label?.[0]}</Avatar> <Text variant='H9' color='text_3'>{headerData.label}</Text>
          </div>}
          <div className="list-item">
            {appointments.map(o => (
              <Appointment disabled={disabled} isMulti key={o.id} data={o} isMonthView={isMonthView} />
            ))}
          </div>
        </div>
      </PopoverAppointmentStyled>
    );
  }, [data, isMonthView, appointments]);

  // The popover opens next to the cell; on the right-most staff columns there is no room for it.
  const placeLeft = useMemo(
    () => shouldFlipPopupLeft(cellRef.current, popoverWidth()),
    [open],
  );

  // Merged cell holding a handful of appointments: every card is visible side by side, shrunk to
  // fit. Above the threshold the cards would be unreadable, so that case keeps the summary box.
  if (split && shouldUseGrid(appointments.length)) {
    // Two cards get a column each and sit at their own start/end inside it, so a 12:15-01:05 card
    // ends at 01:05 even when its neighbour runs to 02:45. Three and four cards keep the 2x2 grid:
    // its rows halve the cell height, so a time offset inside one of them would mean nothing.
    const byTime = appointments.length === 2 && !!cellRange;
    return (
      <GridContainerStyled data-count={appointments.length} onDoubleClick={onStopPropagation}>
        {appointments.map(o => {
          const card = (
            <Appointment
              data={o}
              headerData={headerData}
              isMonthView={isMonthView}
              disabled={disabled}
            />
          );
          if (!byTime) return <GridCellStyled key={o.id}>{card}</GridCellStyled>;
          const { top, height } = computeSlotPosition(o, cellRange as CellRange);
          return (
            <GridCellStyled key={o.id}>
              <div className="slot" style={{ top: `${top}%`, height: `${height}%` }}>{card}</div>
            </GridCellStyled>
          );
        })}
      </GridContainerStyled>
    );
  }

  return (
    <Popover
      arrow={false}
      placement={placeLeft ? 'left' : 'right'}
      open={open}
      onOpenChange={setOpen}
      content={content}
      trigger="click"
      overlayInnerStyle={overlayInnerStyle}
      // @ts-ignore 
      getPopupContainer={() => calendarDOM.current}
    >
      <MultiAppointmentStyled
        ref={cellRef}
        className='MultiAppointmentStyled'
        onDoubleClick={onStopPropagation}
        onClick={onClickOpen}
        style={callerSelectActive.value ? { pointerEvents: 'none' } : undefined}
      >
        <div className="appointment-box">
          {appointments.map((o, i) => (
            <React.Fragment key={o.id}>
              <AppointmentNew data={o} />
              {appointments.length - 1 !== i && <div className="divider" />}
            </React.Fragment>
          ))}
        </div>
        <div className="top appointment-alert-icon"><Icon type='exclamationCircle' /></div>
      </MultiAppointmentStyled>
    </Popover>

  );
};

export default memo(MultiAppointment);

const AppointmentNew = memo(({ data }: { data: ICalendarItemData }) => {
  const time = useMemo(() => {
    const startTime = momentTimezone(data?.startTime);
    const endTime = momentTimezone(data?.endTime);
    return `${startTime.format(FORMAT_TIME)} - ${endTime.format(FORMAT_TIME)}`;
  }, [data]);
  const extraData = useMemo(() => {
    return data.data as IAppointmentItemData;
  }, [data]);
  return (
    <AppointmentNewContainer>
      <Row align={'middle'} wrap={false}>
        <div className='top-content text-ellipsis'>
          <Text className='customer-name appointment-name' variant='CONTENT_2' color='text_3' style={{ fontWeight: '600' }}>{extraData?.customerName?.slice(0, 11)}{extraData?.customerName.length > 11 ? '...' : ''}</Text>
          <Text className='time appointment-time text-ellipsis' variant='CAPTION_3' color='text_3' style={{ fontWeight: '600' }}>{time}</Text>
        </div>
        <SmallSvg style={{ display: 'flex', flexDirection: 'row', position: 'relative' }}>
          {extraData.seriesId && <div className='appointment-icon-sm' style={{ display: 'flex', paddingTop: '1px', width: '16px' }}><RepeatIcon /></div>}
          {extraData.requestStaff && <div className='appointment-icon-sm' style={{ display: 'flex', paddingTop: '1px', width: '16px' }}><RequestStaffIcon /></div>}
          {extraData.confirmed && <div className='appointment-icon-sm' style={{ display: 'flex', paddingTop: '1px', width: '16px' }}><ConfirmIcon /></div>}
        </SmallSvg>
      </Row>
      <div className='top-content text-ellipsis'>
        <Text className='time appointment-time service-name-list text-ellipsis' variant='CAPTION_3' color='text_3' style={{ color: '#1D2129', flex: 1 }}>{extraData.note ? 'Note: ' + extraData.note : extraData.services.map(o => o.serviceName || '').join(', ')}</Text>
        <SmallSvg className='appointment-icon-sm' style={{ width: '16px' }}>
          <AppointmentSourceIcon data={extraData} />
        </SmallSvg>
      </div>
    </AppointmentNewContainer>
  );
});

const SmallSvg = styled.div`
gap: 2px;
display: flex;
align-items: center;
svg {
    width: 16px !important;
    height: 16px !important;
  }
`;

const AppointmentNewContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 2px;
  .top-content {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    padding-right: 2px;
  }
  .customer-name {
     color: #1D2129;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    white-space: nowrap;  
  }
  .time.appointment-time-sm {
    color: #7B7B7B;
  }
  .service-name-list {
    white-space: nowrap;  
    padding-right: 2px;
    color: #1D2129;
  }
`;

const PopoverAppointmentStyled = styled.div`
    width: 50vh;
    max-width: 32rem;
    display: flex;
    flex-direction: column;
    padding:1.5rem;

    border-radius: 5px;
    border: 1px solid #86909C;
    background: #FFF;
    box-shadow: -1px 1px 4px 0px rgba(0, 0, 0, 0.10) inset, 0px 4px 4px 0px rgba(0, 0, 0, 0.25);

    .divider {
      background: #86909C;
      width: 100%;
      height: 1px;
    }

    .head {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 1.5rem;
    }

    .body {
      gap: 16px;
      display: flex;
      flex-direction: column;
    }
    .row-item {
      gap: 11px;
      display: flex;
      align-items: center;
    }
    .list-item {
      gap: 16px;
      display: flex;
      flex-direction: column;
    }
`;

type MultiAppointmentStyledProps = {}
const MultiAppointmentStyled = styled.div<MultiAppointmentStyledProps>`
  height: 100%;
  width: 100%;
  display: flex;

  .divider {
    background: #86909C;
    width: 100%;
    height: 0.5px;
    margin: 4px 0;
  }

  .appointment-box {
    border-radius: 5px;
    background: #FFEFEB;
  
    border: 0.5px solid #F05326;
    box-shadow: inset 3px 0px 0px 0px #F05326;
    overflow: hidden;

    padding: 4px 4px;
    padding-left: 8px;
    flex: 1;
  }
  .top {
    position: absolute;
    top: -10px;
    right:-10px;
    z-index: 1;
  }
  .customerName {
    color: #1D2129;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
  .time {
    color: #F05326;
    font-family: Poppins;
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`;

/**
 * Shared look for a card that shares its cell with other items: shrink it, let it use the full slot
 * height (the card normally reserves 12px for the +/- row), drop the +/- controls entirely, and
 * show only the time and customer name.
 * `display: none` also removes them from hit-testing, so a hidden button cannot swallow a click.
 * Scoped to the card element itself so a grid nested inside a split track cannot compound the zoom.
 */
export const compactCardCss = css`
  .AppointmentStyled,
  .BlockHourItemStyled {
    zoom: ${COMPACT_CARD_ZOOM};
    height: 100%;
  }
  .appointment-bottom-action,
  .block-hour-bottom-action {
    display: none;
  }
  /* Half a column at 0.7 zoom has room for what identifies the booking: time and customer name.
     Services, notes and the style-A note chip are read from the detail view instead. */
  .appointment-service,
  .appointment-service-sm,
  .appointment-note-chip {
    display: none;
  }
`;

const GridContainerStyled = styled.div`
  display: grid;
  gap: 2px;
  width: 100%;
  height: 100%;
  &[data-count="1"] {
    grid-template-columns: 1fr;
    grid-template-rows: 1fr;
  }
  &[data-count="2"] {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: 1fr;
  }
  &[data-count="3"] {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
    /* Odd card out spans the full width instead of leaving a hole. */
    & > :nth-child(3) {
      grid-column: 1 / -1;
    }
  }
  &[data-count="4"] {
    grid-template-columns: repeat(2, 1fr);
    grid-template-rows: repeat(2, 1fr);
  }
`;

const GridCellStyled = styled.div`
  min-width: 0;
  min-height: 0;
  position: relative;
  overflow: hidden;
  ${compactCardCss}
  .slot {
    position: absolute;
    left: 0;
    right: 0;
    overflow: hidden;
  }
`;

const ConfirmIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="12" fill="#148CF1" />
    <path d="M9.72692 18C9.40676 18 9.10261 17.8661 8.8785 17.6317L4.34817 12.8936C3.88394 12.4081 3.88394 11.6045 4.34817 11.1189C4.8124 10.6334 5.58078 10.6334 6.04501 11.1189L9.72692 14.9697L17.955 6.36414C18.4192 5.87862 19.1876 5.87862 19.6518 6.36414C20.1161 6.84967 20.1161 7.6533 19.6518 8.13882L10.5753 17.6317C10.3512 17.8661 10.0471 18 9.72692 18Z" fill="white" />
  </svg>
);
const RequestStaffIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="24" height="24" rx="5" fill="#148CF1" />
    <path d="M4.5 9.85283C4.5 13.5 7.51457 15.4436 9.7213 17.1832C10.5 17.797 11.25 18.375 12 18.375C12.75 18.375 13.5 17.797 14.2787 17.1832C16.4854 15.4436 19.5 13.5 19.5 9.85283C19.5 6.20562 15.3748 3.6191 12 7.12547C8.62512 3.6191 4.5 6.20562 4.5 9.85283Z" fill="white" />
  </svg>
);
